/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ProductLookupClause, ProductsDataActions, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';

import { IActiveImageConfig, IActivePointsData } from '../active-image';

/**
 * ActiveImageInput.
 */
export class ActiveImageInput implements IActionInput {
    public config: IActiveImageConfig;

    private readonly _cacheKey: string = 'ActiveImage';

    public constructor(config: IActiveImageConfig) {
        this.config = config;

        if (this.config.activePoints && ArrayExtensions.hasElements(this.config.activePoints)) {
            for (const activePointData of this.config.activePoints) {
                if (activePointData.itemId) {
                    this._cacheKey = `${this._cacheKey}-${activePointData.itemId.toString()}`;
                }
            }
        }
    }

    /**
     * GetCacheObjectType.
     * @returns - Returns string.
     */
    public getCacheObjectType = (): string => 'ActiveImageFullProductSearchResult';

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => this._cacheKey;

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => 'application';
}

/**
 * CreateInput.
 * @param inputData - IActionContext.
 * @returns - IActionInput.
 */
export const createInput = (inputData: ICreateActionContext): IActionInput => {
    return new ActiveImageInput(inputData.config as IActiveImageConfig);
};

/**
 * Action.
 * @param input - ActiveImageInput.
 * @param context - IActionContext.
 * @returns - ProductSearchResult[].
 */
export const getActiveImageproductAction = async (input: ActiveImageInput, context: IActionContext): Promise<ProductSearchResult[]> => {
    const { config } = input;
    const itemIds: ProductLookupClause[] = [];
    if (config.activePoints) {
        config.activePoints.map((activePointData: IActivePointsData) => itemIds.push({ ItemId: activePointData.itemId }));
    }
    const searchCriteriaInput: ProductSearchCriteria = {};
    const catalogId = getCatalogId(context.requestContext);
    validateCatalogId(catalogId);

    searchCriteriaInput.Context = {
        ChannelId: context.requestContext.apiSettings.channelId,
        CatalogId: catalogId
    };
    if (process.env.NODE_ENV === 'development' && process.env.ENABLE_CLOUD_SEARCH?.toLowerCase() !== 'true') {
        searchCriteriaInput.IncludeAttributes = false;
    } else {
        searchCriteriaInput.IncludeAttributes = true;
    }
    searchCriteriaInput.ItemIds = itemIds;
    if (!config.activePoints || !ArrayExtensions.hasElements(config.activePoints)) {
        throw new Error('Fail to call ActiveImage API no active points configured.');
    }
    // eslint-disable-next-line no-return-await -- Disable because we want to return the promise.
    return await ProductsDataActions.searchByCriteriaAsync({ callerContext: context }, searchCriteriaInput);
};

/**
 * Action.
 * @param id - Id.
 * @param action - Action.
 * @returns - Results.
 */
export const actionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/active-image/get-active-image-products',
    action: getActiveImageproductAction as IAction<ProductSearchResult[]>,
    input: createInput
});

export default actionDataAction;
